/**
 * /app/$tenantSlug/me/letters — Me → Letters (wave-2 offboarding).
 *
 * Every letter HR has SENT you (relieving, experience, F&F statement,
 * warning, probation confirmation), with a page per letter for viewing /
 * acknowledging, plus your exit's F&F statement.
 *
 * It is also the landing page of the RESTRICTED leaver session: once your
 * last working day has passed, the app only answers your own payslips,
 * documents, letters, F&F statement and POSH complaints (backend
 * LeaverAccessGate), so this page carries all of them and calls nothing
 * else. The API client sends a
 * restricted session here when any other request is refused with
 * 403 access_restricted.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText, Lock, Mail, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { useAuth, useFeatures } from "@/lib/auth";
import { hrApi, OFFBOARDING_BASE, payrollApi, toApiError } from "@/lib/api";
import {
  useEmployeeLetters,
  useMyOffboardingAccess,
  useOffboardingCycles,
} from "@/hooks/use-offboarding";
import { useMyComplaints } from "@/hooks/use-posh";
import { complaintStageLabel } from "@/lib/posh-format";
import { fmtDate, fmtDateTime, isAcknowledgeableType, letterTypeLabel, statusLabel } from "@/lib/offboarding-format";
import { formatPaise } from "@/lib/expense-format";
import { DOC_TYPE_LABELS } from "@/lib/hr-format";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import { MyForm16Card } from "@/components/payroll/form16-release";
import { EmptyState } from "@/components/shared/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/letters/")({
  component: MyLettersRoute,
});

const API_BASE_URL =
  import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? "http://localhost:8090";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

async function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function SectionError({ err }: Readonly<{ err: unknown }>) {
  return (
    <Alert variant="destructive">
      <AlertDescription>{toApiError(err).message || "Please refresh and try again."}</AlertDescription>
    </Alert>
  );
}

function MyLettersRoute() {
  const { tenantSlug } = Route.useParams();
  const { user } = useAuth();
  const { has: hasFeature } = useFeatures();
  const me = user?.user_id ?? 0;
  const access = useMyOffboardingAccess(me > 0);
  // /me already says so for a restricted session (the alumni shell) — no
  // need to wait for the access probe before showing the self-service
  // sections.
  const restricted = user?.access_restricted === true || access.data?.restricted === true;
  const restrictedLWD = access.data?.last_working_day ?? user?.restricted_last_working_day;
  const alumniUntil = access.data?.alumni_access_until ?? user?.alumni_access_until;
  const letters = useEmployeeLetters(me, undefined, me > 0);
  const cycles = useOffboardingCycles(me > 0 ? { employee_id: me, include_closed: true } : undefined);
  const sent = (letters.data?.items ?? []).filter((l) => l.sent_to_employee_at);
  const ffCycles = (cycles.data?.items ?? []).filter(
    (c) => c.employee_id === me && c.ff_snapshot_jsonb != null,
  );
  const openExit = (cycles.data?.items ?? []).find(
    (c) => c.employee_id === me && c.status !== "closed" && c.status !== "cancelled",
  );

  return (
    <MeModuleShell
      title="Letters"
      description="Letters HR has sent you, and your exit documents."
    >
      <div className="space-y-5">
        {restricted && (
          <Alert data-testid="leaver-restricted-banner">
            <Lock aria-hidden className="h-4 w-4" />
            <AlertTitle>
              {alumniUntil ? "You have left this company" : "Your last working day has passed"}
            </AlertTitle>
            <AlertDescription>
              {restrictedLWD
                ? `Your last working day was ${fmtDate(restrictedLWD)}. `
                : ""}
              Your account now shows only your own payslips, documents,
              letters and F&amp;F statement — everything is on this page.
              When HR shares your Form 16 it appears under Documents.
              {alumniUntil
                ? ` You can sign in and download these until ${fmtDate(alumniUntil)} — save copies you want to keep.`
                : ""}
            </AlertDescription>
          </Alert>
        )}

        {openExit && !restricted && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your Exit</CardTitle>
              <CardDescription>
                Last working day {fmtDate(openExit.last_working_day)} ·{" "}
                {statusLabel(openExit.status)}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Letters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail aria-hidden className="h-4 w-4" /> Letters
            </CardTitle>
          </CardHeader>
          <CardContent>
            {letters.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : letters.isError ? (
              <SectionError err={letters.error} />
            ) : sent.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No Letters Yet"
                description="When HR sends you a letter it appears here and in your notifications."
              />
            ) : (
              <ul className="divide-y">
                {sent.map((l) => (
                  <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{letterTypeLabel(l.letter_type)}</p>
                      <p className="text-xs text-muted-foreground">Sent {fmtDateTime(l.sent_to_employee_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isAcknowledgeableType(l.letter_type) && !l.acknowledged_at && (
                        <Badge variant="destructive">Needs Acknowledgement</Badge>
                      )}
                      {l.acknowledged_at && <Badge variant="outline">Acknowledged</Badge>}
                      <Button asChild size="sm" variant="outline">
                        <Link
                          to="/app/$tenantSlug/me/letters/$id"
                          params={{ tenantSlug, id: String(l.id) }}
                        >
                          Open
                        </Link>
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* F&F statements */}
        {ffCycles.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Wallet aria-hidden className="h-4 w-4" /> Full and Final Settlement
              </CardTitle>
              <CardDescription>
                The current statement (it includes any correction HR has recorded).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="divide-y">
                {ffCycles.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-2 py-2.5 text-sm">
                    <span>Exit with last working day {fmtDate(c.last_working_day)}</span>
                    <Button asChild size="sm" variant="outline" className="gap-1.5">
                      <a
                        href={`${API_BASE_URL}${OFFBOARDING_BASE}/cycles/${c.id}/ff-statement.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Download aria-hidden className="h-3.5 w-3.5" /> Statement PDF
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {restricted && <RestrictedPayslips me={me} />}
        {restricted && <MyForm16Card />}
        {restricted && <RestrictedDocuments />}
        {restricted && hasFeature("posh") && <RestrictedComplaints />}
      </div>
    </MeModuleShell>
  );
}

/**
 * The leaver's OWN POSH complaints (read-only status) — a complainant who
 * resigns mid-inquiry keeps seeing where her complaint stands. Rendered
 * only in the restricted session (the complaint pages themselves are out
 * of reach there) and only while the POSH module is on.
 */
function RestrictedComplaints() {
  const q = useMyComplaints();
  const items = q.data?.complaints ?? [];
  if (!q.isLoading && !q.isError && items.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Your POSH Complaints</CardTitle>
        <CardDescription>
          Status of the complaints you filed. The Internal Committee keeps
          you informed of any update.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : q.isError ? (
          <SectionError err={q.error} />
        ) : (
          <ul className="divide-y">
            {items.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 py-2.5 text-sm">
                <span className="min-w-0 truncate">
                  {c.complaint_number}{" "}
                  <span className="text-muted-foreground">· filed {fmtDate(c.created_at)}</span>
                </span>
                <Badge variant="outline">{complaintStageLabel(c.status)}</Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function RestrictedPayslips({ me }: Readonly<{ me: number }>) {
  const q = useQuery({
    queryKey: ["me", "payslips", "restricted"],
    queryFn: ({ signal }) => payrollApi.listMyPayslips(signal),
  });
  const [busy, setBusy] = useState<number | null>(null);
  const slips = q.data?.payslips ?? [];
  async function download(runId: number, y: number, m: number) {
    setBusy(runId);
    try {
      const blob = await payrollApi.downloadSalarySlip(runId, me, { download: true });
      await saveBlob(blob, `salary-slip-${y}-${String(m).padStart(2, "0")}.pdf`);
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setBusy(null);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Payslips</CardTitle>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : q.isError ? (
          <SectionError err={q.error} />
        ) : slips.length === 0 ? (
          <EmptyState title="No Payslips" description="Payslips appear here once payroll is locked." />
        ) : (
          <ul className="divide-y">
            {slips.map((p) => (
              <li key={p.run_id} className="flex items-center justify-between gap-2 py-2.5 text-sm">
                <span>
                  {MONTHS[p.period_month - 1]} {p.period_year} · net {formatPaise(p.net_paise)}
                </span>
                <Button size="sm" variant="outline" className="gap-1.5" disabled={busy === p.run_id}
                  onClick={() => void download(p.run_id, p.period_year, p.period_month)}>
                  <Download aria-hidden className="h-3.5 w-3.5" /> PDF
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function RestrictedDocuments() {
  const q = useQuery({
    queryKey: ["me", "documents", "restricted"],
    queryFn: ({ signal }) => hrApi.listHRDocuments(undefined, signal),
  });
  const [busy, setBusy] = useState<number | null>(null);
  const docs = q.data?.documents ?? [];
  async function download(id: number, name: string) {
    setBusy(id);
    try {
      await saveBlob(await hrApi.downloadHRDocument(id), name);
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setBusy(null);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Documents</CardTitle>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : q.isError ? (
          <SectionError err={q.error} />
        ) : docs.length === 0 ? (
          <EmptyState title="No Documents" description="Documents HR shares with you appear here." />
        ) : (
          <ul className="divide-y">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2 py-2.5 text-sm">
                <span className="min-w-0 truncate">
                  {d.file_name}{" "}
                  <span className="text-muted-foreground">· {DOC_TYPE_LABELS[d.doc_type] ?? d.doc_type}</span>
                </span>
                <Button size="sm" variant="outline" className="gap-1.5" disabled={busy === d.id}
                  onClick={() => void download(d.id, d.file_name)}>
                  <Download aria-hidden className="h-3.5 w-3.5" /> Download
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
